import { useCallback, useEffect, useState } from "react";
import type { Key } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Link } from "@/components/ui/link";
import { ItemDetail } from "@/components/ui/list-box";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { mutationResponse } from "@/utils/trpc";
import type { OrgDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

export function ClientTestsTable({ org }: { org: OrgDetail }) {
  const tests = org.tests;

  const updateOrder = trpc.admin.org.updateTestOrder.useMutation();
  const updatePrice = trpc.admin.org.updateTestPrice.useMutation();

  const { options } = useAdminGridOptions(
    tests,
    [
      {
        field: "testSpec.name",
        headerName: "Name",
        rowDrag: true,
        flex: 1,
        minWidth: 200,
        cellRenderer: (params) => (
          <Link
            href={{
              to: "/admin/tests/$id/edit",
              params: { id: params.id as string },
            }}
          >
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.testSpecId,
          name: params.data.testSpec.name,
        }),
      },
      {
        field: "testSpec.code",
        headerName: "Code",
        width: 140,
        maxWidth: 200,
      },
      {
        colId: "price",
        headerName: "Price",
        editable: true,
        type: "numericColumn",
        cellRendererParams: (params) => ({
          testSpecPrice: params.data.testSpec.price ?? 0,
          price: params.data.price,
        }),
        cellRenderer: (params) => {
          return (
            <>
              {params.price !== null ? (
                <div className="flex items-center gap-1 justify-end w-full">
                  <Text className="line-through" muted>
                    ${params.testSpecPrice.toFixed(2)}
                  </Text>
                  <Text>${params.price.toFixed(2)}</Text>
                </div>
              ) : (
                <Text>${params.testSpecPrice.toFixed(2)}</Text>
              )}
            </>
          );
        },

        valueGetter: (params) => {
          return params.data?.price ?? params.data?.testSpec.price ?? 0;
        },
      },
    ],
    {
      sideBar: undefined,
      domLayout: "autoHeight",
      readOnlyEdit: true,
      getRowId: (row) => row.data.id.toString(),
      onCellEditRequest: (e) => {
        if (e.column.getColId() !== "price") {
          return;
        }

        const newPrice = e.newValue === "" ? null : Number(e.newValue);

        // Validate that the price is not NaN (catches invalid user input like non-numeric strings)
        if (newPrice !== null && Number.isNaN(newPrice)) {
          toast.error(
            "Invalid price: please enter a valid number or leave blank",
          );
          return;
        }

        e.api.showLoadingOverlay();

        updatePrice.mutate(
          {
            id: e.data.id,
            testSpecId: e.data.testSpecId,
            price: newPrice,
          },
          mutationResponse({
            errorTitle: "Error updating price",
            successMessage: "Price updated for client",
            onSuccess: () => e.api.hideOverlay(),
            onError: () => e.api.hideOverlay(),
          }),
        );
      },
      onRowDragEnd: (e) => {
        const ids: number[] = [];
        e.api.forEachNode((node) => {
          if (node.data?.testSpecId) {
            ids.push(node.data.testSpecId);
          }
        });
        updateOrder.mutate(
          { id: org.id, testIds: ids },
          mutationResponse({
            errorTitle: "Error reordering test specs",
            successMessage: "Test specs reordered",
          }),
        );
      },
    },
  );

  return (
    <TabLayout
      description="This client will be able to select the following test specs."
      actions={<UpdateTestsDialog org={org} />}
    >
      <AdminGrid {...options} />
    </TabLayout>
  );
}

function UpdateTestsDialog({ org }: { org: OrgDetail }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<Key[]>(org.tests.map((t) => t.testSpecId));
  const { data } = trpc.tests.all.useQuery();

  const updateTests = trpc.admin.org.updateTests.useMutation();

  useEffect(() => {
    if (open) {
      setKeys(org.tests.map((t) => t.testSpecId));
    }
  }, [open, org.tests]);

  const onSelectionChange = useCallback(
    (keys: "all" | Set<Key>) => {
      if (keys === "all") {
        setKeys(data?.map((t) => t.id) || []);
      } else {
        setKeys(Array.from(keys));
      }
    },
    [data],
  );

  const onUpdate = useCallback(() => {
    updateTests.mutate(
      { id: org.id, testIds: keys.map((k) => Number(k)) },
      mutationResponse({
        errorTitle: "Failed to update tests",
        successMessage: "Tests updated",
        onSuccess: () => setOpen(false),
      }),
    );
  }, [keys, org.id, updateTests]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary" onPress={() => setOpen(true)}>
        Edit test specs
      </Button>
      <Modal isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Update test specs</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <GridList
              aria-label="Select tests"
              items={data || []}
              selectionMode="multiple"
              selectedKeys={keys}
              onSelectionChange={onSelectionChange}
            >
              {(test) => (
                <GridListItem
                  key={test.id}
                  textValue={`${test.name} (${test.code})`}
                >
                  {test.name}
                  <ItemDetail>{test.code}</ItemDetail>
                </GridListItem>
              )}
            </GridList>
          </DialogBody>
          <DialogFooter>
            <Button isPending={updateTests.isPending} onPress={onUpdate}>
              Update
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
